import { QueryClient, QueryFunction } from "@tanstack/react-query";

function isServerUnavailableError(status: number): boolean {
  return status === 502 || status === 503 || status === 504 || status === 0;
}

interface ApiRequestOptions {
  dispatchServerUnavailable?: boolean;
}

async function throwIfResNotOk(res: Response, options: ApiRequestOptions = {}) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    const error = new Error(`${res.status}: ${text}`) as Error & {
      status: number;
      data?: any;
      trialExpired?: boolean;
    };
    error.status = res.status;

    // Check for server unavailable errors (during deployments)
    if (isServerUnavailableError(res.status) && options.dispatchServerUnavailable !== false) {
      window.dispatchEvent(new CustomEvent("serverUnavailable"));
      throw error;
    }

    // Try to parse JSON response for structured error data
    try {
      error.data = JSON.parse(text);

      // Check for access blocked (trial expired or subscription inactive)
      if (res.status === 403) {
        // Check for normalized payload with reason
        if (error.data.reason) {
          window.dispatchEvent(new CustomEvent("trialExpired", { detail: error.data }));
        }
        // Backward compatibility: check legacy flags
        else if (error.data.trialExpired) {
          error.trialExpired = true;
          window.dispatchEvent(
            new CustomEvent("trialExpired", {
              detail: {
                reason: "trialExpired",
                ...error.data,
              },
            }),
          );
        } else if (error.data.subscriptionInactive) {
          window.dispatchEvent(
            new CustomEvent("trialExpired", {
              detail: {
                reason: "subscriptionInactive",
                ...error.data,
              },
            }),
          );
        }
      }
    } catch {
      // If not JSON, just use the text as message
      error.data = { message: text };
    }

    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options: ApiRequestOptions = {},
): Promise<Response> {
  try {
    const res = await fetch(url, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    await throwIfResNotOk(res, options);
    return res;
  } catch (error: any) {
    // Network errors (server completely unavailable)
    if (
      options.dispatchServerUnavailable !== false &&
      (error.name === "TypeError" ||
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("NetworkError"))
    ) {
      window.dispatchEvent(new CustomEvent("serverUnavailable"));
    }
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    try {
      const res = await fetch(queryKey[0] as string, {
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (error: any) {
      // Network errors (server completely unavailable)
      if (
        error.name === "TypeError" ||
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("NetworkError")
      ) {
        window.dispatchEvent(new CustomEvent("serverUnavailable"));
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
